import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import theme from '../styles/theme'
import { AUDIT_ACTIONS } from '../services/auditLog'

const ActivityLogPill = styled.button`
  height: 40px;
  padding: 0 1.25rem;
  background: ${theme.color.accent.primary};
  color: white;
  border: none;
  border-radius: 20px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  z-index: ${theme.zIndex.navigation};
  box-shadow: 0 2px 8px ${theme.color.shadow.dark};
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #b8002b;
    transform: translateY(-1px);
  }
`

const ActivityLogModal = styled.div<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 1000;
`

const ActivityLogContent = styled.div`
  background: ${theme.color.background.light};
  border-radius: 8px;
  width: 90%;
  max-width: 800px;
  max-height: 80vh;
  overflow: hidden;
  box-shadow: 0 8px 32px ${theme.color.shadow.dark};
`

const ActivityLogHeader = styled.div`
  background: ${theme.color.accent.primary};
  color: ${theme.color.text.light};
  padding: 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const HeaderTitle = styled.h3`
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${theme.color.text.light};
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
`

const LogList = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 0;
`

const LogItem = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid ${theme.color.border.light};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background: ${theme.color.background.lightGrey};
  }
`

const LogDetails = styled.div`
  font-size: 1.3rem;
  color: ${theme.color.text.base};
  font-weight: 500;
  margin-bottom: 0.5rem;
  line-height: 1.4;
`

const LogMeta = styled.div`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
`

const LogAdmin = styled.span`
  font-weight: 500;
`

const LogDate = styled.span`
  font-size: 1rem;
`

const LogTarget = styled.span`
  font-family: 'Courier New', monospace;
  font-size: 1rem;
  background: ${theme.color.background.lightGrey};
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
`

const ActionBadge = styled.span<{ actionType: string }>`
  display: inline-block;
  font-size: 0.9rem;
  padding: 0.25rem 0.75rem;
  border-radius: 4px;
  font-weight: 600;
  margin-right: 0.5rem;

  ${props => {
    switch (props.actionType) {
      case 'USER_LOOKUP':
        return `
          background: #e3f2fd;
          color: #1565c0;
        `
      case 'USER_GROUP_ADDED':
        return `
          background: #e8f5e9;
          color: #2e7d32;
        `
      case 'USER_GROUP_REMOVED':
        return `
          background: #ffebee;
          color: #c62828;
        `
      default:
        return `
          background: ${theme.color.background.lightGrey};
          color: ${theme.color.text.grey};
        `
    }
  }}
`

const GroupBadge = styled.span`
  display: inline-block;
  font-size: 0.9rem;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  background: #fff3e0;
  color: #e65100;
  font-weight: 500;
  margin-left: 0.5rem;
`

const OktaProfileBadge = styled.span`
  display: inline-block;
  font-size: 0.85rem;
  padding: 0.2rem 0.5rem;
  border-radius: 4px;
  background: #f3e5f5;
  color: #7b1fa2;
  margin-left: 0.5rem;
`

const LoadingMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${theme.color.text.grey};
  font-style: italic;
  font-size: 1.2rem;
`

const ErrorMessage = styled.div`
  padding: 1.5rem;
  color: #d70036;
  background: #ffe6e6;
  margin: 1rem;
  border-radius: 4px;
  font-size: 1.2rem;
`

const EmptyMessage = styled.div`
  padding: 2rem;
  text-align: center;
  color: ${theme.color.text.grey};
  font-size: 1.2rem;
`

const DateFilterContainer = styled.div`
  display: flex;
  gap: 1rem;
  padding: 1rem 1.5rem;
  background: ${theme.color.background.lightGrey};
  border-bottom: 1px solid ${theme.color.border.light};
  align-items: center;
  flex-wrap: wrap;
`

const DateInput = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1rem;
`

const DateLabel = styled.label`
  font-size: 1.1rem;
  color: ${theme.color.text.grey};
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ExportButton = styled.button`
  background: #2e7d32;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;

  &:hover {
    background: #1b5e20;
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`

const SearchInput = styled.input`
  padding: 0.5rem;
  border: 1px solid ${theme.color.border.light};
  border-radius: 4px;
  font-size: 1rem;
  width: 200px;
`

interface AuditLogEntry {
  id: number
  user_email: string
  tab_name: string
  action: string
  details: {
    group_name?: string
    group_id?: string
    requester_department?: string
    requester_title?: string
    target_user_name?: string
    target_user_status?: string
  } | null
  admin_user: string
  created_at: string
}

const AdminActivityLog: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [logs, setLogs] = useState<AuditLogEntry[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [startDate, setStartDate] = useState<string>('')
  const [endDate, setEndDate] = useState<string>('')
  const [searchQuery, setSearchQuery] = useState<string>('')

  const fetchLogs = async (filterStartDate?: string, filterEndDate?: string) => {
    setIsLoading(true)
    setError(null)

    try {
      // Fetch all user management actions
      const actions = [
        AUDIT_ACTIONS.USER_LOOKUP,
        AUDIT_ACTIONS.USER_GROUP_ADDED,
        AUDIT_ACTIONS.USER_GROUP_REMOVED
      ]

      const queryParams = new URLSearchParams({
        limit: '1000'
      })

      if (filterStartDate) {
        queryParams.append('startDate', new Date(filterStartDate).toISOString())
      }
      if (filterEndDate) {
        const endOfDay = new Date(filterEndDate)
        endOfDay.setHours(23, 59, 59, 999)
        queryParams.append('endDate', endOfDay.toISOString())
      }

      // Fetch logs for each action type
      const allLogs: AuditLogEntry[] = []

      for (const action of actions) {
        const params = new URLSearchParams(queryParams)
        params.set('action', action)

        const response = await fetch(`/api/audit-log?${params}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          }
        })

        if (response.ok) {
          const data = await response.json()
          const logsArray = data.logs || []
          allLogs.push(...logsArray)
        }
      }

      // Sort by date (most recent first)
      const sortedLogs = allLogs.sort((a: AuditLogEntry, b: AuditLogEntry) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      )
      setLogs(sortedLogs)
    } catch (err) {
      console.error('Failed to fetch admin activity logs:', err)
      setError(`Failed to load admin activity history: ${err instanceof Error ? err.message : 'Unknown error'}`)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (isOpen && logs.length === 0) {
      fetchLogs(startDate, endDate)
    }
  }, [isOpen])

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch {
      return 'Unknown date'
    }
  }

  const getActionLabel = (action: string) => {
    switch (action) {
      case AUDIT_ACTIONS.USER_LOOKUP:
        return 'User Lookup'
      case AUDIT_ACTIONS.USER_GROUP_ADDED:
        return 'Group Added'
      case AUDIT_ACTIONS.USER_GROUP_REMOVED:
        return 'Group Removed'
      default:
        return action
    }
  }

  const handleModalClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      setIsOpen(false)
    }
  }

  const handleRefresh = () => {
    setLogs([])
    fetchLogs(startDate, endDate)
  }

  const handleDateFilter = () => {
    setLogs([])
    fetchLogs(startDate, endDate)
  }

  // Filter logs by search query (email)
  const filteredLogs = logs.filter(log => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase().trim()
    return (
      log.user_email?.toLowerCase().includes(query) ||
      log.admin_user?.toLowerCase().includes(query)
    )
  })

  const formatDateForCSV = (dateString: string) => {
    try {
      const date = new Date(dateString)
      return date.toISOString().replace('T', ' ').substring(0, 19)
    } catch {
      return 'Unknown'
    }
  }

  const exportToCSV = () => {
    if (filteredLogs.length === 0) return

    const headers = ['Date/Time', 'Action', 'Target Email', 'Target Name', 'Group', 'Admin User', 'Department', 'Title']

    const csvRows = filteredLogs.map(log => [
      formatDateForCSV(log.created_at),
      getActionLabel(log.action),
      log.user_email,
      log.details?.target_user_name || '',
      log.details?.group_name || '',
      log.admin_user,
      log.details?.requester_department || '',
      log.details?.requester_title || ''
    ])

    const csvContent = [
      headers.join(','),
      ...csvRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    const now = new Date()
    const timestamp = now.toISOString().split('T')[0]
    let filename = `admin_activity_log_${timestamp}`
    if (startDate) filename += `_from_${startDate}`
    if (endDate) filename += `_to_${endDate}`
    filename += '.csv'

    link.setAttribute('href', url)
    link.setAttribute('download', filename)
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  return (
    <>
      <ActivityLogPill onClick={() => setIsOpen(true)} title="Admin Activity Log">
        <span style={{ fontSize: '1.1rem' }}>📋</span>
        Admin Log
      </ActivityLogPill>

      <ActivityLogModal isOpen={isOpen} onClick={handleModalClick}>
        <ActivityLogContent>
          <ActivityLogHeader>
            <HeaderTitle>Admin Activity Log</HeaderTitle>
            <div>
              <CloseButton onClick={handleRefresh} title="Refresh" style={{ marginRight: '0.5rem' }}>↻</CloseButton>
              <CloseButton onClick={() => setIsOpen(false)}>×</CloseButton>
            </div>
          </ActivityLogHeader>

          <DateFilterContainer>
            <DateLabel>
              From:
              <DateInput
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </DateLabel>
            <DateLabel>
              To:
              <DateInput
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </DateLabel>
            <CloseButton
              onClick={handleDateFilter}
              title="Apply Filter"
              style={{ color: theme.color.text.base, fontSize: '1.2rem' }}
            >
              Filter
            </CloseButton>
            <SearchInput
              type="text"
              placeholder="Search by email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <ExportButton onClick={exportToCSV} disabled={filteredLogs.length === 0}>
              Export CSV
            </ExportButton>
          </DateFilterContainer>

          {isLoading ? (
            <LoadingMessage>Loading admin activity...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : filteredLogs.length === 0 ? (
            <EmptyMessage>{searchQuery ? 'No matching activity found.' : 'No admin activity found.'}</EmptyMessage>
          ) : (
            <LogList>
              {filteredLogs.map((log) => (
                <LogItem key={log.id}>
                  <LogDetails>
                    <ActionBadge actionType={log.action}>{getActionLabel(log.action)}</ActionBadge>
                    {log.details?.group_name && (
                      <GroupBadge>{log.details.group_name}</GroupBadge>
                    )}
                    {log.details?.target_user_name && (
                      <span style={{ marginLeft: '0.5rem', color: theme.color.text.grey }}>
                        ({log.details.target_user_name})
                      </span>
                    )}
                  </LogDetails>
                  <LogMeta>
                    <div>
                      <LogAdmin>By: {log.admin_user}</LogAdmin>
                      {log.details?.requester_department && (
                        <OktaProfileBadge>{log.details.requester_department}</OktaProfileBadge>
                      )}
                      {log.details?.requester_title && (
                        <OktaProfileBadge>{log.details.requester_title}</OktaProfileBadge>
                      )}
                    </div>
                    <div>
                      <LogTarget>Target: {log.user_email}</LogTarget>
                    </div>
                    <LogDate>{formatDate(log.created_at)}</LogDate>
                  </LogMeta>
                </LogItem>
              ))}
            </LogList>
          )}
        </ActivityLogContent>
      </ActivityLogModal>
    </>
  )
}

export default AdminActivityLog
